import {
  Button,
  Checkbox,
  Modal,
  MultiSelect,
  NumberInput,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { formatQuantity } from './requisitions.js';
import {
  type RfqFormValues,
  rfqFormDefaults,
  rfqLineChoices,
  toRfqInput,
  validateRfqForm,
} from './rfqs.js';
import type { RfqSource } from './source.js';
import type { RequisitionDetail, RfqDetail, Supplier } from './types.js';

/**
 * Onaylı talepten RFQ (`satin-alma-modulu.md` §5; sorular #77): kapanış tarihi, para birimi,
 * teslim yeri ve koşulları, davet edilecek tedarikçiler (yalnız onaylı ve etkin; #79) ve teklif
 * istenecek kalemler. Talep başına tek açık RFQ.
 */
export function CreateRfqDialog({
  requisition,
  today,
  source,
  onClose,
  onCreated,
}: {
  requisition: RequisitionDetail;
  /** Ofisin bugünü (`YYYY-MM-DD`). */
  today: string;
  source: RfqSource;
  onClose: () => void;
  onCreated: (rfq: RfqDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.create.${key}`, options);
  const [values, setValues] = useState<RfqFormValues>(rfqFormDefaults(requisition, today));
  const choices = rfqLineChoices(requisition);
  const [suppliers, setSuppliers] = useState<Supplier[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<RfqFormValues>) => setValues((v) => ({ ...v, ...patch }));
  const error = (field: string) =>
    errors[field]
      ? t(`purchasing.field.${errors[field]}`, { defaultValue: errors[field] })
      : undefined;

  useEffect(() => {
    let alive = true;
    source
      .suppliers()
      .then((list) => {
        if (alive) setSuppliers(list.items.filter((s) => s.approved && s.active));
      })
      .catch((failure) => {
        if (alive) {
          setSuppliers([]);
          setSaveError(errorCode(failure));
        }
      });
    return () => {
      alive = false;
    };
  }, [source]);

  const text = (field: keyof RfqFormValues, label: string, max: number) => (
    <TextInput
      label={label}
      maxLength={max}
      value={values[field] as string}
      onChange={(event) => set({ [field]: event.currentTarget.value } as Partial<RfqFormValues>)}
      error={error(field)}
    />
  );

  return (
    <Modal
      opened
      onClose={onClose}
      size="xl"
      title={k('title', { no: requisition.requisitionNo })}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const found = validateRfqForm(values);
          setErrors(found);
          if (Object.keys(found).length > 0) return;
          setBusy(true);
          setSaveError(null);
          try {
            onCreated(await source.create(toRfqInput(values, requisition)));
          } catch (failure) {
            setSaveError(errorCode(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset className={classes.dialogSection}>
          <legend>{k('general')}</legend>
          <div className={classes.dialogGrid}>
            <DayInput
              label={k('closingDate')}
              required
              minDate={today}
              value={values.closingDate}
              onChange={(value) => set({ closingDate: value ?? '' })}
              error={error('closingDate')}
            />
            <TextInput
              label={k('currency')}
              required
              maxLength={3}
              value={values.currency}
              onChange={(event) => set({ currency: event.currentTarget.value.toUpperCase() })}
              error={error('currency')}
            />
            <NumberInput
              label={k('validity')}
              min={0}
              allowDecimal={false}
              value={values.validityDays}
              onChange={(value) => set({ validityDays: typeof value === 'number' ? value : '' })}
              error={error('validityDays')}
            />
            {text('deliveryLocation', k('deliveryLocation'), 120)}
            {text('paymentTerms', k('paymentTerms'), 200)}
            {text('warranty', k('warranty'), 200)}
          </div>
          {text('deliveryRequirement', k('deliveryRequirement'), 300)}
          <Textarea
            label={k('technicalSpec')}
            autosize
            minRows={2}
            maxLength={2000}
            value={values.technicalSpec}
            onChange={(event) => set({ technicalSpec: event.currentTarget.value })}
            mt="sm"
          />
        </fieldset>

        <div className={classes.dialogSection}>
          {suppliers === null ? (
            <Skeleton height={36} aria-label={k('loading')} />
          ) : (
            <MultiSelect
              label={k('suppliers')}
              description={k('suppliersHint')}
              placeholder={k('suppliersPlaceholder')}
              required
              searchable
              data={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              value={values.supplierIds}
              onChange={(supplierIds) => set({ supplierIds })}
              error={error('supplierIds')}
            />
          )}
        </div>

        <fieldset className={classes.dialogSection}>
          <legend>{k('items')}</legend>
          <div className={classes.checkList}>
            {/* Onaylı listeden (ADR 0111 §A); revizyon yoksa talep satırları */}
            {choices.map((line) => (
              <Checkbox
                key={line.key}
                label={`${line.lineNo ?? '+'}. ${line.name} · ${formatQuantity(line.quantity, locale)} ${line.unit}`}
                aria-label={k('itemOf', { name: line.name })}
                checked={values.lineIds.includes(line.key)}
                onChange={(event) =>
                  set({
                    lineIds: event.currentTarget.checked
                      ? choices
                          .map((l) => l.key)
                          .filter((id) => id === line.key || values.lineIds.includes(id))
                      : values.lineIds.filter((id) => id !== line.key),
                  })
                }
              />
            ))}
          </div>
          {errors.lineIds ? (
            <p className={classes.error} role="alert">
              {t(`purchasing.field.${errors.lineIds}`)}
            </p>
          ) : null}
        </fieldset>

        {saveError ? (
          <p className={classes.error} role="alert">
            {t(`purchasing.errors.${saveError}`, { defaultValue: t('purchasing.errors.GENERIC') })}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy} disabled={suppliers === null}>
            {k('create')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
